<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { RouterLink } from "vue-router";
import { History, LogOut, Settings } from "lucide-vue-next";
import { useAuthStore } from "@/stores/auth";

const props = defineProps({
  loginRedirect: { type: String, default: "/tokenomics" },
});

const auth = useAuthStore();
const open = ref(false);
const menuRef = ref(null);
const accountLabel = computed(() => auth.accountLabel);
const accountInitial = computed(() => accountLabel.value.slice(0, 1));
const loginUrl = computed(() => auth.loginUrl(props.loginRedirect));

function closeOnPointerDown(event) {
  if (!menuRef.value?.contains(event.target)) open.value = false;
}

function closeOnEscape(event) {
  if (event.key === "Escape") open.value = false;
}

watch(open, (value) => {
  if (value) {
    document.addEventListener("pointerdown", closeOnPointerDown);
    document.addEventListener("keydown", closeOnEscape);
  } else {
    document.removeEventListener("pointerdown", closeOnPointerDown);
    document.removeEventListener("keydown", closeOnEscape);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", closeOnPointerDown);
  document.removeEventListener("keydown", closeOnEscape);
});

async function logout() {
  open.value = false;
  await auth.logout();
}
</script>

<template>
  <a v-if="!auth.user" class="login-link" :href="loginUrl">登录</a>

  <div v-else ref="menuRef" class="account-menu">
    <button
      type="button"
      class="avatar-button"
      :aria-expanded="open"
      aria-haspopup="menu"
      aria-label="账户菜单"
      @click="open = !open"
    >
      <img v-if="auth.user.avatarUrl" :src="auth.user.avatarUrl" alt="" />
      <span v-else>{{ accountInitial }}</span>
    </button>

    <div v-if="open" class="account-popover" role="menu">
      <div class="account-summary">
        <img v-if="auth.user.avatarUrl" :src="auth.user.avatarUrl" alt="" />
        <span v-else>{{ accountInitial }}</span>
        <div>
          <strong>{{ accountLabel }}</strong>
          <small>{{ auth.user.email || auth.user.phone || "已登录" }}</small>
        </div>
      </div>
      <RouterLink class="account-menu-item" :to="{ name: 'settings' }" role="menuitem" @click="open = false">
        <Settings :size="16" />
        设置
      </RouterLink>
      <RouterLink class="account-menu-item" :to="{ name: 'changelog' }" role="menuitem" @click="open = false">
        <History :size="16" />
        更新日志
      </RouterLink>
      <button class="account-menu-item" type="button" role="menuitem" @click="logout">
        <LogOut :size="16" />
        退出登录
      </button>
    </div>
  </div>
</template>
